import type { Object3D } from 'three';

import {
    isBoxSelectionCandidateObject,
    isEntitySelectionCandidateEligible,
    isEntitySelectionCandidateObject,
} from './EntitySelectionQueryHelpers';

/**
 * 基于共享场景成员源维护实体选择候选类型索引。
 *
 * 类型分类只在对象进入场景时执行；Hover、点击与框选查询只验证已跟踪候选的动态资格。
 * 本对象不拥有场景对象、几何或材质，释放时仅退订并清空引用。
 */
export class EntitySelectionCandidateIndex {
    /** 已按稳定运行时类型分类的候选，Set 插入顺序与成员回放/重挂顺序一致。 */
    private readonly candidates = new Set<Object3D>();
    /** 共享成员源退订函数。 */
    private unsubscribe: (() => void) | null;
    /** 累计成员类型分类次数。 */
    private membershipClassifications = 0;
    /** 累计动态资格检查次数。 */
    private eligibilityChecks = 0;
    /** 累计查询次数。 */
    private queries = 0;

    /**
     * @param membershipSource Viewer 统一维护的非拥有型场景成员源。
     */
    public constructor(membershipSource: SceneObjectMembershipSource) {
        this.unsubscribe = membershipSource.subscribe({
            onObjectAdded: (object) => this.trackObject(object),
            onObjectRemoved: (object) => this.candidates.delete(object),
        });
    }

    /**
     * 将当前点击与 Hover 共用候选写入调用方缓冲。
     *
     * @param target 可复用输出数组；调用开始时会先清空。
     */
    public writePointingTargets(target: Object3D[]): void {
        this.queries += 1;
        target.length = 0;
        for (const object of this.candidates) {
            this.eligibilityChecks += 1;
            if (isEntitySelectionCandidateEligible(object)) target.push(object);
        }
    }

    /**
     * 收集当前框选候选。
     *
     * @param target 可复用输出数组；调用开始时会先清空。
     */
    public writeBoxTargets(target: Object3D[]): void {
        this.queries += 1;
        target.length = 0;
        for (const object of this.candidates) {
            this.eligibilityChecks += 1;
            if (
                isEntitySelectionCandidateEligible(object)
                && isBoxSelectionCandidateObject(object)
            ) {
                target.push(object);
            }
        }
    }

    /** @returns 只在显式性能检查时创建的工作量统计。 */
    public getStats(): EntitySelectionCandidateIndexStats {
        return {
            trackedCandidates: this.candidates.size,
            membershipClassifications: this.membershipClassifications,
            eligibilityChecks: this.eligibilityChecks,
            queries: this.queries,
        };
    }

    /** 幂等退订并释放候选引用，不删除场景对象或 GPU 资源。 */
    public dispose(): void {
        if (!this.unsubscribe) return;
        this.unsubscribe();
        this.unsubscribe = null;
        this.candidates.clear();
    }

    /**
     * 对刚进入场景的单个成员执行一次稳定类型分类。
     *
     * @param object 新增成员。
     */
    private trackObject(object: Object3D): void {
        this.membershipClassifications += 1;
        if (isEntitySelectionCandidateObject(object)) this.candidates.add(object);
    }
}
